import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { renderLinkifiedText } from '../../../utils/linkify';
import GifAttachment from '../GifAttachment';
import MediaAttachmentGrid from '../MediaAttachmentGrid';

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatReceiptTime(value) {
  const date = toDate(value);
  if (!date) return '-';
  return date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function getOtherReaderIds(msg = {}) {
  const readBy = Array.isArray(msg.readBy) ? msg.readBy : [];
  return readBy.filter((uid) => uid && uid !== msg.senderId);
}

function getReadAtValue(msg = {}) {
  const readAt = msg.readAt && typeof msg.readAt === 'object' ? msg.readAt : {};
  const entries = Object.entries(readAt)
    .filter(([uid, value]) => uid && uid !== msg.senderId && toDate(value))
    .sort(([, left], [, right]) => toDate(left).getTime() - toDate(right).getTime());
  return entries[0]?.[1] || null;
}

function ReceiptTick({ tone = 'sent' }) {
  return (
    <span className={`message-receipt-tick message-receipt-tick--${tone}`} aria-hidden="true">
      ✓
    </span>
  );
}

function MessageBubbleContent({ msg }) {
  return (
    <>
      {msg.content && <div className="chat-bubble__text">{renderLinkifiedText(msg.content)}</div>}
      {msg.media?.provider === 'giphy' && (
        <GifAttachment gif={msg.media} />
      )}
      <MediaAttachmentGrid
        media={msg.media}
        mediaAttachments={msg.mediaAttachments}
        itemClassName="chat-bubble__upload"
      />
    </>
  );
}

export default function ChatMessages({ messages = [], currentUserId }) {
  const containerRef = useRef(null);
  const [expandedMessageId, setExpandedMessageId] = useState('');
  const messageScrollKey = useMemo(() => messages.map((msg) => msg.id).join('|'), [messages]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;
    const frameIds = [];
    const timeoutIds = [];
    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    const scrollToBottom = () => {
      if (cancelled) return;
      container.scrollTop = container.scrollHeight;
    };

    scrollToBottom();
    frameIds.push(requestFrame(() => {
      scrollToBottom();
      frameIds.push(requestFrame(scrollToBottom));
    }));
    timeoutIds.push(window.setTimeout(scrollToBottom, 120));
    timeoutIds.push(window.setTimeout(scrollToBottom, 500));

    const resizeObserver = typeof window.ResizeObserver === 'function'
      ? new window.ResizeObserver(scrollToBottom)
      : null;
    if (resizeObserver) {
      resizeObserver.observe(container);
      Array.from(container.children).forEach((child) => resizeObserver.observe(child));
    }

    return () => {
      cancelled = true;
      frameIds.forEach((id) => cancelFrame(id));
      timeoutIds.forEach((id) => window.clearTimeout(id));
      resizeObserver?.disconnect();
    };
  }, [messageScrollKey]);

  return (
    <div className="chat-messages" ref={containerRef}>
      {messages.map((msg) => {
        const isCurrentUser = msg.senderId === currentUserId;
        const bubbleClass = isCurrentUser
          ? 'me'
          : msg.isSupport
          ? 'support'
          : 'them';
        const otherReaderIds = getOtherReaderIds(msg);
        const isRead = isCurrentUser && otherReaderIds.length > 0;
        const readAtValue = isRead ? getReadAtValue(msg) : null;
        const isExpanded = isCurrentUser && expandedMessageId === msg.id;
        const sentTime = formatReceiptTime(msg.sentAt);
        const readTime = readAtValue ? formatReceiptTime(readAtValue) : '-';
        const receiptTone = isRead ? 'read' : 'sent';
        const receiptStatus = isRead ? 'Read' : 'Sent';

        return (
          <div
            key={msg.id}
            className={`chat-message ${bubbleClass} ${isExpanded ? 'is-expanded' : ''}`}
          >
            {/* Header above the bubble */}
            {!isCurrentUser && msg.isSupport && (
              <div className="chat-bubble-header">
                <span className="support-badge">Support</span>
              </div>
            )}

            {msg.isBroadcast && (
              <div className="chat-bubble-header">
                <span className="chat-broadcast-badge">{msg.broadcastLabel || 'Broadcast'}</span>
              </div>
            )}

            {isCurrentUser ? (
              <>
                <div className="chat-message__outgoing-content">
                  <div className={`chat-bubble ${bubbleClass}`}>
                    <MessageBubbleContent msg={msg} />
                  </div>
                  <button
                    type="button"
                    className="message-receipt-summary"
                    onClick={() => setExpandedMessageId((current) => current === msg.id ? '' : msg.id)}
                    aria-expanded={isExpanded}
                    aria-label={`${receiptStatus} message. ${isExpanded ? 'Hide' : 'Show'} message details.`}
                  >
                    <ReceiptTick tone={receiptTone} />
                  </button>
                </div>

                {isExpanded && (
                  <div className="message-receipt-details">
                    <div className="message-receipt-details__row">
                      <ReceiptTick tone="sent" />
                      <span>Sent</span>
                      <time dateTime={toDate(msg.sentAt)?.toISOString()}>{sentTime}</time>
                    </div>
                    <div className="message-receipt-details__row">
                      <ReceiptTick tone="read" />
                      <span>Read</span>
                      {readAtValue ? (
                        <time dateTime={toDate(readAtValue)?.toISOString()}>{readTime}</time>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={`chat-bubble ${bubbleClass}`}>
                <MessageBubbleContent msg={msg} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

ChatMessages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      senderId: PropTypes.string.isRequired,
      content: PropTypes.string,
      media: PropTypes.shape({
        provider: PropTypes.string,
        url: PropTypes.string,
        previewUrl: PropTypes.string,
        title: PropTypes.string,
        mediaType: PropTypes.string,
        storagePath: PropTypes.string,
        fileName: PropTypes.string,
      }),
      mediaAttachments: PropTypes.arrayOf(PropTypes.shape({
        provider: PropTypes.string,
        uploadId: PropTypes.string,
        mediaType: PropTypes.string,
        storagePath: PropTypes.string,
        thumbnailPath: PropTypes.string,
        fileName: PropTypes.string,
      })),
      sentAt: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.shape({ toDate: PropTypes.func }),
        PropTypes.string,
        PropTypes.number,
      ]),
      readBy: PropTypes.arrayOf(PropTypes.string),
      readAt: PropTypes.object,
      isSupport: PropTypes.bool,
      isBroadcast: PropTypes.bool,
      broadcastLabel: PropTypes.string,
    })
  ),
  currentUserId: PropTypes.string.isRequired,
};

ReceiptTick.propTypes = {
  tone: PropTypes.oneOf(['sent', 'read']),
};

MessageBubbleContent.propTypes = {
  msg: PropTypes.shape({
    content: PropTypes.string,
    media: PropTypes.shape({
      provider: PropTypes.string,
      url: PropTypes.string,
      previewUrl: PropTypes.string,
      title: PropTypes.string,
    }),
    mediaAttachments: PropTypes.array,
  }).isRequired,
};
